import { useState, useEffect, type FormEvent } from 'react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/feature/Toast';
import { supabase } from '@/lib/supabase';

interface SalesModalProps {
  open: boolean;
  onClose: () => void;
  onSaleComplete?: () => void;
}

type MetodoPago = 'Efectivo' | 'QR';

export default function SalesModal({ open, onClose, onSaleComplete }: SalesModalProps) {
  const { items, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [metodoPago, setMetodoPago] = useState<MetodoPago>('Efectivo');
  const [montoRecibido, setMontoRecibido] = useState('');
  const [cambio, setCambio] = useState(0);

  const [nombreNit, setNombreNit] = useState('');
  const [numeroNit, setNumeroNit] = useState('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const monto = parseFloat(montoRecibido);
    if (!isNaN(monto) && monto >= totalPrice) {
      setCambio(monto - totalPrice);
    } else {
      setCambio(0);
    }
  }, [montoRecibido, totalPrice]);

  const montoValido = (() => {
    const monto = parseFloat(montoRecibido);
    return !isNaN(monto) && monto >= totalPrice;
  })();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (metodoPago === 'Efectivo' && !montoValido) return;
    if (items.length === 0) return;

    setLoading(true);

    try {
      const ventaData = {
        total: totalPrice,
        metodo_pago: metodoPago,
        monto_recibido: metodoPago === 'Efectivo' ? parseFloat(montoRecibido) : totalPrice,
        cambio: metodoPago === 'Efectivo' ? cambio : 0,
        nombre_nit: nombreNit.trim() || null,
        numero_nit: numeroNit.trim() || null,
        usuario_id: user?.id || null,
      };

      const { data: venta, error: ventaError } = await supabase
        .from('ventas')
        .insert(ventaData)
        .select('id')
        .maybeSingle();

      if (ventaError) {
        console.error('Error insertando venta:', ventaError);
        throw new Error(`Error al registrar venta: ${ventaError.message}`);
      }
      if (!venta || !venta.id) {
        throw new Error('La venta no se registró correctamente');
      }

      const detalleData = items.map((item) => ({
        venta_id: venta.id,
        producto_id: item.id,
        cantidad: item.cantidad,
        precio_unitario: item.precio,
        subtotal: item.precio * item.cantidad,
      }));

      const { error: detalleError } = await supabase
        .from('detalle_venta')
        .insert(detalleData);

      if (detalleError) {
        console.error('Error insertando detalle:', detalleError);
        throw new Error(`Error al registrar detalle: ${detalleError.message}`);
      }

      for (const item of items) {
        const { data: producto, error: prodError } = await supabase
          .from('productos')
          .select('stock')
          .eq('id', item.id)
          .maybeSingle();

        if (prodError) {
          console.error('Error consultando stock:', prodError);
          continue;
        }

        if (producto) {
          const nuevoStock = Math.max(0, producto.stock - item.cantidad);
          const { error: updateError } = await supabase
            .from('productos')
            .update({ stock: nuevoStock, updated_at: new Date().toISOString() })
            .eq('id', item.id);

          if (updateError) {
            console.error('Error actualizando stock:', updateError);
            continue;
          }

          const { error: kardexError } = await supabase.from('kardex').insert({
            producto_id: item.id,
            tipo: 'salida',
            cantidad: item.cantidad,
            stock_anterior: producto.stock,
            stock_nuevo: nuevoStock,
            motivo: 'Venta registrada',
            usuario_id: user?.id || null,
          });

          if (kardexError) {
            console.error('Error en kardex:', kardexError);
          }
        }
      }

      const { error: audError } = await supabase.from('auditoria').insert({
        usuario: user?.nombre || 'Sistema',
        accion: 'Realizó una venta',
        modulo: 'Ventas',
      });

      if (audError) {
        console.error('Error en auditoría:', audError);
      }

      clearCart();
      onClose();
      resetForm();
      onSaleComplete?.();
      addToast('¡Venta realizada exitosamente!', 'success');
    } catch (err: unknown) {
      let msg = 'Error al procesar la venta';
      if (err instanceof Error) msg = err.message;
      if (typeof err === 'object' && err && 'message' in err) msg = String((err as { message: unknown }).message);
      console.error('Venta error completo:', err);
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMetodoPago('Efectivo');
    setMontoRecibido('');
    setCambio(0);
    setNombreNit('');
    setNumeroNit('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={handleClose} />

      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-background-200 rounded-t-2xl">
          <h2 className="font-heading text-lg font-semibold text-foreground-800">Finalizar Venta</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="bg-background-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground-500">Total a pagar</span>
              <span className="text-2xl font-bold text-primary-600">
                Bs. {totalPrice.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-foreground-400">
              {items.length} producto{items.length !== 1 ? 's' : ''} en el carrito
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              Método de pago
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['Efectivo', 'QR'] as MetodoPago[]).map((metodo) => (
                <button
                  key={metodo}
                  type="button"
                  onClick={() => setMetodoPago(metodo)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                    metodoPago === metodo
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-background-200 text-foreground-500 hover:border-background-300'
                  }`}
                >
                  <i className={metodo === 'Efectivo' ? 'ri-money-dollar-circle-line' : 'ri-qr-code-line'}></i>
                  <span>{metodo === 'Efectivo' ? 'Efectivo' : 'QR'}</span>
                </button>
              ))}
            </div>
          </div>

          {metodoPago === 'Efectivo' && (
            <div className="space-y-3">
              <div>
                <label htmlFor="montoRecibido" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Monto recibido (Bs.)
                </label>
                <input
                  id="montoRecibido"
                  type="number"
                  step="0.01"
                  min="0"
                  value={montoRecibido}
                  onChange={(e) => setMontoRecibido(e.target.value)}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="0.00"
                />
              </div>

              {cambio > 0 && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-primary-700 font-medium">Cambio:</span>
                    <span className="text-lg font-bold text-primary-600">
                      Bs. {cambio.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {montoRecibido && !montoValido && (
                <p className="text-xs text-accent-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  El monto recibido debe ser mayor o igual al total
                </p>
              )}
            </div>
          )}

          <div className="border-t border-background-200 pt-4">
            <button
              type="button"
              onClick={() => setNombreNit('')}
              className="flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-file-list-3-line"></i>
              <span>Facturación (opcional)</span>
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label htmlFor="nombreNit" className="block text-xs text-foreground-500 mb-1">
                  Nombre / Razón Social
                </label>
                <input
                  id="nombreNit"
                  type="text"
                  value={nombreNit}
                  onChange={(e) => setNombreNit(e.target.value)}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-all"
                  placeholder="Nombre o empresa"
                />
              </div>
              <div>
                <label htmlFor="numeroNit" className="block text-xs text-foreground-500 mb-1">
                  NIT / CI
                </label>
                <input
                  id="numeroNit"
                  type="text"
                  value={numeroNit}
                  onChange={(e) => setNumeroNit(e.target.value)}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-all"
                  placeholder="Número de NIT"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || (metodoPago === 'Efectivo' && !montoValido) || items.length === 0}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Procesando venta...</span>
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                <span>Confirmar Venta</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}